import { useCallback, useEffect, useState } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetRevenueThisMonthInsightsUseCase } from "../../domain/use-cases/GetRevenueThisMonthInsightsUseCase";
import type { RevenueThisMonthInsightsPanelProps } from "../components/revenue_components/RevenueThisMonthInsightsPanel";

const repository = new ClientOverviewRepositoryImpl();
const getRevenueThisMonthInsightsUseCase =
  new GetRevenueThisMonthInsightsUseCase(repository);

export interface UseRevenueThisMonthInsightsOptions {
  /** When false, the insights API is not called. */
  enabled?: boolean;
}

const defaultInsights: RevenueThisMonthInsightsPanelProps = {
  monthLabel: "— · Insights",
  insights: [
    { title: "Billing Pace", description: "—", accentColor: "neutral" },
    { title: "Invoices Raised", description: "—", accentColor: "neutral" },
  ],
  loading: false,
};

export function useRevenueThisMonthInsights(
  options?: UseRevenueThisMonthInsightsOptions,
): RevenueThisMonthInsightsPanelProps & { error: string | null } {
  const enabled = options?.enabled ?? true;

  const [data, setData] =
    useState<RevenueThisMonthInsightsPanelProps>(defaultInsights);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const entity = await getRevenueThisMonthInsightsUseCase.execute();
      setData({
        monthLabel: entity.monthLabel,
        insights: entity.insights.slice(0, 2),
        loading: false,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load revenue insights",
      );
      setData(defaultInsights);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return { ...data, loading, error };
}
